import { IDENTITY_FIELDS } from '@/features/entity-resolution/identityAssessment'
import type {
  ResolutionBand,
  ResolutionIdentityChecks,
  ResolutionSummary,
} from '@/services/api/entity-resolution-assistant'
import { isResolutionCount as count, isResolutionRecord as record } from './resolution-values'

function fieldCounts(value: unknown, total: number): value is Record<string, number> {
  return (
    record(value) &&
    Object.entries(value).every(
      ([field, number]) =>
        IDENTITY_FIELDS.some(known => known === field) && count(number) && number <= total
    )
  )
}

/** Old messages remain readable, but only complete fresh checks enable merging. */
export function parseIdentityChecks(
  value: unknown,
  bands: ResolutionBand[],
  total: number
): ResolutionIdentityChecks | undefined | null {
  if (value === undefined)
    return bands.every(
      band => band.bulk_eligible_group_count === undefined && band.blocked_group_count === undefined
    )
      ? undefined
      : null
  if (!record(value)) return null
  const { conflict_groups, review_groups, supported_groups, unknown_groups } = value
  if (
    !count(conflict_groups) ||
    !count(review_groups) ||
    !count(supported_groups) ||
    !count(unknown_groups) ||
    conflict_groups + review_groups + supported_groups + unknown_groups !== total ||
    !fieldCounts(value.conflict_fields, total) ||
    !fieldCounts(value.review_fields, total) ||
    !bands.every(validBandEligibility) ||
    bands.reduce((sum, band) => sum + (band.blocked_group_count ?? 0), 0) <
      conflict_groups + review_groups
  )
    return null
  return {
    conflict_groups,
    review_groups,
    supported_groups,
    unknown_groups,
    conflict_fields: value.conflict_fields,
    review_fields: value.review_fields,
  }
}

function validBandEligibility(band: ResolutionBand): boolean {
  return (
    count(band.bulk_eligible_group_count) &&
    count(band.blocked_group_count) &&
    band.bulk_eligible_group_count + band.blocked_group_count === band.group_count &&
    (band.id !== 'unknown' || band.bulk_eligible_group_count === 0)
  )
}

export function identityChecksAvailable(summary: ResolutionSummary): boolean {
  return summary.identity_checks !== undefined && summary.bands.every(validBandEligibility)
}

export function bulkEligibleGroups(band: ResolutionBand): number {
  return band.bulk_eligible_group_count ?? 0
}
